import React, { useState, useEffect } from 'react';
import { Typography, Divider, Card, Row, Col, Spin, Button, Progress, Tag, Space } from 'antd';
import {
  TrophyOutlined,
  GiftOutlined,
  SettingOutlined,
  CreditCardOutlined,
  RightOutlined,
  ThunderboltOutlined,
  UserOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getUserProfile } from '../auth';
import { gamificationApi, getCurrentSubscription, creditsApi } from '../api';
import { getRankByExp, RankIcon, ExpProgress } from './Gamification/RankComponents';
import moment from 'moment-timezone';

const { Title, Text } = Typography;

const MyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [gamificationStatus, setGamificationStatus] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profile, gamification, sub, credits] = await Promise.all([
          getUserProfile(),
          gamificationApi.getStatus().catch(() => null),
          getCurrentSubscription().catch(() => null),
          creditsApi.getMyCredits().catch(() => null),
        ]);
        setUserProfile(profile);
        setGamificationStatus(gamification);
        setSubscription(sub);
        setCreditInfo(credits);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const cardStyle = {
    borderRadius: 12,
    boxShadow: 'none',
    border: '1px solid #f0f0f0',
    height: '100%',
  };

  const getLinkCardStyle = (cardId) => ({
    ...cardStyle,
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: hoveredCard === cardId ? '#fafafa' : '#fff',
  });

  // プラン名を取得
  const getPlanName = () => {
    if (subscription?.plan?.display_name) {
      return subscription.plan.display_name;
    }
    if (userProfile?.plan?.display_name) {
      return userProfile.plan.display_name;
    }
    return 'フリー';
  };

  // プランタイプを取得
  const getPlanType = () => {
    return subscription?.plan?.type || userProfile?.plan?.type || 'free';
  };

  // 次回プラン更新日/有効期限を取得
  const getNextRenewalDate = () => {
    const planType = getPlanType();
    if (planType === 'free') {
      return '-';
    }
    if (subscription?.current_period_end) {
      return moment(subscription.current_period_end).tz('Asia/Tokyo').format('YYYY/MM/DD');
    }
    return '-';
  };

  // プラン更新日のラベルを取得
  const getPlanDateLabel = () => {
    if (subscription?.status === 'trialing') {
      return 'トライアル終了日';
    }
    const planType = getPlanType();
    if (planType === 'custom') {
      return 'プラン有効期限';
    }
    return '次回プラン更新日';
  };

  // クレジットラベルを取得（カスタムプランの場合は「期間内クレジット」）
  const getCreditLabel = () => {
    const planType = getPlanType();
    if (planType === 'custom') {
      return '期間内クレジット';
    }
    return '月間クレジット';
  };

  // クレジットリセットラベルを取得（カスタムプランの場合は「カスタムプラン有効期限」）
  const getCreditResetLabel = () => {
    const planType = getPlanType();
    if (planType === 'custom') {
      return 'カスタムプラン有効期限';
    }
    return '次回クレジットリセット';
  };

  return (
    <>
      <Helmet>
        <title>マイページ | magicss</title>
        <meta name="robots" content="noindex" />
        <meta property="og:title" content="マイページ | magicss" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${process.env.PUBLIC_URL}/img/ogp.png`}
        />
        <meta
          property="og:description"
          content="コンテンツ生成AIツール「magicss」のマイページです。"
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div>
        <Text style={{ margin: '16px 0' }}>マイページ</Text>
        <Title level={1} className="gradient-text" style={{ fontSize: '24px' }}>
          マイページ
        </Title>
        <Text style={{ color: '#6d8eb7' }}>
          アカウント情報やプラン、ランクシステムの状況を確認できます。
        </Text>
        <Divider style={{ margin: '24px 0' }} />

        {/* ユーザー情報サマリー */}
        <Card style={{ ...cardStyle, marginBottom: 24 }}>
          <Row gutter={[24, 16]} align="middle">
            <Col xs={24} md={12}>
              <Space size={16} align="start">
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #0072ff 0%, #00d4ff 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <UserOutlined style={{ fontSize: 28, color: '#fff' }} />
                </div>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {userProfile?.last_name} {userProfile?.first_name}
                  </Title>
                  <Text type="secondary">{userProfile?.email}</Text>
                  {userProfile?.company_name && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {userProfile.company_name}
                        {userProfile.department_name && ` / ${userProfile.department_name}`}
                      </Text>
                    </div>
                  )}
                </div>
              </Space>
            </Col>
            <Col xs={24} md={12} style={{ textAlign: 'right' }}>
              <Button
                type="default"
                icon={<SettingOutlined />}
                onClick={() => navigate('/settings')}
              >
                登録情報を変更
              </Button>
            </Col>
          </Row>
        </Card>

        <Row gutter={[24, 24]}>
          {/* プラン・クレジット情報 */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <CreditCardOutlined style={{ color: '#0072ff' }} />
                  <span>プラン・クレジット</span>
                </Space>
              }
              style={getLinkCardStyle('plan')}
              onMouseEnter={() => setHoveredCard('plan')}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => navigate('/subscription-management')}
              extra={<RightOutlined style={{ color: '#999' }} />}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text type="secondary">現在のプラン</Text>
                  <div>
                    <Text strong style={{ fontSize: 18 }}>
                      {getPlanName()}
                    </Text>
                    {subscription?.status === 'trialing' && (
                      <Tag color="purple" style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                        無料トライアル中
                      </Tag>
                    )}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">{getPlanDateLabel()}</Text>
                  <div>
                    <Text strong style={{ fontSize: 18 }}>
                      {getNextRenewalDate()}
                    </Text>
                  </div>
                </Col>
              </Row>
              <Divider style={{ margin: '16px 0' }} />
              <Row gutter={[16, 16]} align="middle">
                <Col span={10}>
                  <Progress
                    type="circle"
                    percent={
                      creditInfo && creditInfo.plan_limit > 0 && creditInfo.plan_limit !== 999999999
                        ? Math.round((creditInfo.credit_used / creditInfo.plan_limit) * 100)
                        : 0
                    }
                    size={80}
                    strokeColor={{
                      '0%': '#52c41a',
                      '70%': '#faad14',
                      '90%': '#ff4d4f',
                    }}
                    format={() => (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 'bold', color: '#333' }}>
                          {creditInfo?.plan_limit === 999999999
                            ? '∞'
                            : `${Math.round(((creditInfo?.credit_used ?? 0) / (creditInfo?.plan_limit || 1)) * 100)}%`}
                        </div>
                      </div>
                    )}
                  />
                </Col>
                <Col span={14}>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{getCreditLabel()}</Text>
                      <div>
                        <Text strong style={{ fontSize: 16 }}>
                          {creditInfo?.credit_used ?? 0}
                          <Text type="secondary" style={{ fontSize: 14, fontWeight: 'normal' }}>
                            {' / '}
                            {creditInfo?.plan_limit === 999999999 ? '∞' : (creditInfo?.plan_limit ?? 0)}
                          </Text>
                        </Text>
                      </div>
                    </div>
                    {creditInfo?.bonus_credit > 0 && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ボーナス<span style={{ opacity: 0.7, marginLeft: 2 }}>(無期限)</span>
                        </Text>
                        <div>
                          <Text strong style={{ fontSize: 16, color: '#722ed1' }}>
                            +{creditInfo.bonus_credit.toLocaleString()}
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{getCreditResetLabel()}</Text>
                    <div>
                      <Text strong style={{ fontSize: 14 }}>
                        {creditInfo?.next_reset_date
                          ? moment(creditInfo.next_reset_date).tz('Asia/Tokyo').format('YYYY/MM/DD')
                          : '-'}
                        {creditInfo?.days_until_reset > 0 && (
                          <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                            {' '}(残り{creditInfo.days_until_reset}日)
                          </Text>
                        )}
                      </Text>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* ランクシステム */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <TrophyOutlined style={{ color: '#faad14' }} />
                  <span>ランクシステム</span>
                </Space>
              }
              style={getLinkCardStyle('rank')}
              onMouseEnter={() => setHoveredCard('rank')}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => navigate('/rank-system')}
              extra={<RightOutlined style={{ color: '#999' }} />}
            >
              {gamificationStatus ? (
                (() => {
                  const { currentRank, nextRank } = getRankByExp(gamificationStatus.total_exp);
                  return (
                    <>
                      <Row gutter={[16, 16]} align="middle">
                        <Col span={12}>
                          <Text type="secondary">現在のランク</Text>
                          <div style={{ marginTop: 8 }}>
                            <Space>
                              <RankIcon rank={currentRank.rank} size={32} />
                              <span
                                style={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: currentRank.rank === 'white_1' ? '#595959' : currentRank.color,
                                  textShadow: currentRank.rank === 'white_1' ? '0 0 1px rgba(0,0,0,0.3)' : 'none',
                                }}
                              >
                                {currentRank.display_name}
                              </span>
                            </Space>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">累計EXP</Text>
                          <div>
                            <Text strong style={{ fontSize: 18 }}>
                              <ThunderboltOutlined style={{ color: '#faad14', marginRight: 4 }} />
                              {gamificationStatus.total_exp} EXP
                            </Text>
                          </div>
                        </Col>
                      </Row>
                      <Divider style={{ margin: '16px 0' }} />
                      <div>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          {nextRank ? `次のランク（${nextRank.display_name}）まで` : '最高ランク到達！'}
                        </Text>
                        <ExpProgress totalExp={gamificationStatus.total_exp} showNumbers={true} size="default" />
                      </div>
                    </>
                  );
                })()
              ) : (
                <Text type="secondary">ランク情報を取得できませんでした</Text>
              )}
            </Card>
          </Col>

          {/* 友達紹介 */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <GiftOutlined style={{ color: '#eb2f96' }} />
                  <span>友達紹介</span>
                </Space>
              }
              style={getLinkCardStyle('referral')}
              onMouseEnter={() => setHoveredCard('referral')}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => navigate('/referral')}
              extra={<RightOutlined style={{ color: '#999' }} />}
            >
              <Text>
                友達を紹介して、お互いにボーナスクレジットをゲット！
              </Text>
              <div style={{ marginTop: 16 }}>
                <Tag color="magenta" style={{ fontSize: 14, padding: '4px 12px' }}>
                  紹介者・被紹介者ともに 200 クレジットプレゼント
                </Tag>
              </div>
            </Card>
          </Col>

          {/* クイックアクション */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <CalendarOutlined style={{ color: '#52c41a' }} />
                  <span>クイックアクション</span>
                </Space>
              }
              style={cardStyle}
            >
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <Button
                  block
                  onClick={() => navigate('/plan')}
                  style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }}
                >
                  <Space>
                    <CreditCardOutlined />
                    <span>プランを変更する</span>
                  </Space>
                </Button>
                <Button
                  block
                  onClick={() => navigate('/bonus-credit')}
                  style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }}
                >
                  <Space>
                    <ThunderboltOutlined />
                    <span>ボーナスクレジットを購入する</span>
                  </Space>
                </Button>
                <Button
                  block
                  onClick={() => navigate('/settings')}
                  style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }}
                >
                  <Space>
                    <SettingOutlined />
                    <span>登録情報を変更する</span>
                  </Space>
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default MyPage;
